"use client";

import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { apiClient, type Resource, type MergeResult } from "@/lib/api-client";
import { GitMerge, Loader2, AlertTriangle } from "lucide-react";

interface MergeResourcesDialogProps {
  source: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onMerged?: (result: MergeResult) => void;
}

/**
 * Two-step merge: pick a target master, type the target slug to confirm.
 *
 * Empty masters (``url === ""``) are excluded from the picker — those rows
 * survived backfill without a configured URL and aren't safely mergeable
 * targets. The server enforces this too (409 on empty target), but the UI
 * filter keeps the picker clean.
 */
export function MergeResourcesDialog({
  source,
  open,
  onOpenChange,
  onMerged,
}: MergeResourcesDialogProps) {
  const { toast } = useToast();
  const [resources, setResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(false);
  const [target, setTarget] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [merging, setMerging] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    apiClient
      .listResources()
      .then((list) => setResources(Array.isArray(list) ? list : []))
      .catch((err) => {
        toast({
          title: "Error",
          description: err.message || "Failed to load merge targets",
          variant: "destructive",
        });
        setResources([]);
      })
      .finally(() => setLoading(false));
    setTarget("");
    setConfirmText("");
  }, [open, toast]);

  // Eligible targets: any other master with a configured URL. Empty
  // masters are deliberately filtered out — see component docstring.
  const eligibleTargets = useMemo(
    () =>
      resources.filter((r) => {
        if (r.name === source) return false;
        const url = r.url || r.mcp_url || "";
        return !!url.trim();
      }),
    [resources, source],
  );

  const targetResource = useMemo(
    () => eligibleTargets.find((r) => r.name === target) || null,
    [eligibleTargets, target],
  );

  const confirmReady = !!targetResource && confirmText.trim() === target;

  const handleMerge = async () => {
    if (!confirmReady) return;
    setMerging(true);
    try {
      const result = await apiClient.mergeResource(source, target);
      toast({
        title: "Merged",
        description: `Moved ${result.moved_connections} connection(s) and ${result.moved_tools} tool(s); dropped ${result.dropped_tools} duplicate tool row(s).`,
      });
      onMerged?.(result);
    } catch (err: any) {
      toast({
        title: err.status === 409 ? "Cannot merge" : "Error",
        description: err.message || "Failed to merge resource",
        variant: "destructive",
      });
    } finally {
      setMerging(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Merge <span className="font-mono text-base">{source}</span> into…
          </DialogTitle>
          <DialogDescription>
            Repoints every linked agent_connection to the target master,
            moves tool inventory rows (target wins on collisions), and
            deletes <span className="font-mono">{source}</span>. Cedar
            policies and audit history on{" "}
            <span className="font-mono">{source}</span> are not migrated;
            <span className="font-mono">{source}</span> disappears.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="merge-target">Target resource</Label>
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading…
              </div>
            ) : eligibleTargets.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">
                No eligible targets. Configure a URL on another master first.
              </p>
            ) : (
              <select
                id="merge-target"
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">Select a target…</option>
                {eligibleTargets.map((r) => (
                  <option key={r.name} value={r.name}>
                    {r.name} ({r.url || r.mcp_url})
                  </option>
                ))}
              </select>
            )}
            <p className="text-xs text-muted-foreground">
              Empty masters (no URL) are excluded — they aren't safe merge
              targets.
            </p>
          </div>

          {targetResource && (
            <div className="rounded border bg-muted/30 p-3 text-xs space-y-1">
              <div className="flex items-center gap-2 font-semibold">
                <GitMerge className="w-3 h-3" />
                Preview
              </div>
              <p className="text-muted-foreground">
                <span className="font-mono">{source}</span> →{" "}
                <span className="font-mono">{target}</span>
              </p>
              <p className="text-muted-foreground">
                All connections currently linked to{" "}
                <span className="font-mono">{source}</span> will repoint to{" "}
                <span className="font-mono">{target}</span>. Tool rows on{" "}
                <span className="font-mono">{source}</span> move over;
                duplicates with the target are dropped (target's
                classification wins).
              </p>
            </div>
          )}

          {targetResource && (
            <div className="space-y-2">
              <Label htmlFor="merge-confirm" className="text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" />
                Type{" "}
                <span className="font-mono font-semibold">{target}</span> to
                confirm
              </Label>
              <Input
                id="merge-confirm"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={target}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleMerge}
            disabled={!confirmReady || merging}
            variant="destructive"
          >
            {merging && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Merge
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
